import { useState } from "react";
import { SupplierImport, uploadUrls } from "@/components/admin/SupplierImport";
import { X, Upload, Loader2, Plus, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, imgUrl, errMsg } from "@/lib/api";
import { BRANDS, MACHINES, SUSPENSIONS } from "@/lib/i18n";
import { OPTION_KEYS, OPTION_LT } from "@/lib/options";

const MACHINE_LT = { tractor: "Traktoriai", harvester: "Kombainai", excavator: "Ekskavatoriai", forklift: "Krautuvai", construction: "Statybinė technika" };
const SUSP_LT = { pneumatic: "Pneumatinė", mechanical: "Mechaninė" };
const VAT = 0.21;
const r2 = (n) => Math.round(n * 100) / 100;

const PriceFields = ({ f, setF }) => {
  const [gross, setGross] = useState(f.price === "" ? "" : String(r2(Number(f.price) * (1 + VAT))));
  const setNet = (v) => { setF({ ...f, price: v }); setGross(v === "" ? "" : String(r2(Number(v) * (1 + VAT)))); };
  const setGr = (v) => { setGross(v); setF({ ...f, price: v === "" ? "" : String(r2(Number(v) / (1 + VAT))) }); };
  const pct = Number(f.discount_pct || 0);
  const sale = r2(Number(f.price || 0) * (1 - pct / 100));
  return (<>
    <Field label="Kaina be PVM, EUR *"><input data-testid="form-price-input" type="number" step="0.01" min="0" className={input} value={f.price} onChange={(e) => setNet(e.target.value)} /></Field>
    <Field label="Kaina su PVM (21 %), EUR"><input data-testid="form-price-gross-input" type="number" step="0.01" min="0" className={input} value={gross} onChange={(e) => setGr(e.target.value)} /></Field>
    <Field label="Akcija, nuolaida %"><input data-testid="form-discount-input" type="number" step="1" min="0" max="90" placeholder="0" className={input} value={f.discount_pct || ""} onChange={(e) => setF({ ...f, discount_pct: e.target.value })} /></Field>
    <div data-testid="form-sale-preview" className="self-end pb-2 text-sm text-slate-400">
      {pct > 0 ? <>Akcijos kaina: <span className="text-red-400 font-mono-x">{sale.toFixed(2)} €</span> be PVM · <span className="font-mono-x">{r2(sale * (1 + VAT)).toFixed(2)} €</span> su PVM</> : "Akcija netaikoma"}
    </div>
  </>);
};

const EMPTY = { name: "", brand: "Grammer", machine_type: "tractor", suspension: "pneumatic", sku: "", price: "", stock: 0, description_lt: "", description_en: "", specs: [], images: [], options: [], active: true, model: "", fits: "", compatible: [], price_on_request: false, stocked: false, stock_rank: 0, premium: true, universal: false, discount_pct: 0, gift_lt: "", gift_en: "", gift_image: "", free_shipping: false };
const input = "w-full h-10 px-3 bg-[#1F2230] border border-[#3B4157] text-sm outline-none focus:border-orange-500";

const Field = ({ label, children, className = "" }) => (
  <label className={`text-xs uppercase tracking-wider text-slate-400 space-y-1.5 block ${className}`}><span>{label}</span>{children}</label>
);

const GiftFields = ({ f, setF }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((x) => ({ ...x, gift_image: data.path }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="sm:col-span-2 border border-emerald-500/30 p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-emerald-400">Dovana prie sėdynės (palikite tuščia, jei nėra)</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="form-gift-lt-input" className={input} placeholder="Dovana LT, pvz. Sėdynės užvalkalas" value={f.gift_lt || ""} onChange={(e) => setF({ ...f, gift_lt: e.target.value })} />
        <input data-testid="form-gift-en-input" className={input} placeholder="Gift EN, e.g. Seat cover" value={f.gift_en || ""} onChange={(e) => setF({ ...f, gift_en: e.target.value })} />
      </div>
      <div className="flex items-center gap-3">
        {f.gift_image && <img src={imgUrl(f.gift_image)} alt="" className="w-14 h-14 object-contain bg-white" />}
        <label className="h-9 px-3 inline-flex items-center border border-[#3B4157] text-sm cursor-pointer hover:border-emerald-400">
          {busy ? "Įkeliama..." : "Dovanos nuotrauka"}
          <input data-testid="form-gift-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
        {f.gift_image && <button type="button" data-testid="form-gift-image-remove" onClick={() => setF({ ...f, gift_image: "" })} className="text-xs text-red-400">Pašalinti</button>}
      </div>
    </div>
  );
};

const Images = ({ images, setImages }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd);
        paths.push(data.path);
      }
      setImages([...images, ...paths]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const [links, setLinks] = useState("");
  const fromLinks = async () => {
    const urls = links.split(/\s+/).filter((u) => /^https?:\/\//.test(u));
    if (!urls.length) return toast.error("Įklijuokite bent vieną nuorodą (https://...)");
    setBusy(true);
    const { paths, failed } = await uploadUrls(urls);
    setImages([...images, ...paths]);
    setBusy(false);
    setLinks("");
    if (paths.length) toast.success(`Įkelta nuotraukų: ${paths.length}`);
    if (failed) toast.error(`Nepavyko įkelti: ${failed}`);
  };
  return (
    <div className="space-y-3">
    <div className="flex flex-wrap gap-2">
      {images.map((im, i) => (
        <div key={im + i} className="relative w-20 h-20 border border-[#3B4157]">
          <img src={imgUrl(im)} alt="" className="w-full h-full object-contain img-stage" />
          {i === 0 && <span className="absolute bottom-0 left-0 bg-orange-500 text-[#1F2230] text-[9px] px-1 font-bold">PAGR.</span>}
          <button type="button" data-testid={`form-remove-image-${i}`} onClick={() => setImages(images.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 w-5 h-5 grid place-items-center bg-red-500 text-white"><X className="w-3 h-3" /></button>
        </div>
      ))}
      <label className="w-20 h-20 border border-dashed border-slate-600 grid place-items-center cursor-pointer hover:border-orange-500 text-slate-400 hover:text-orange-500">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
        <input data-testid="form-image-upload" type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload([...e.target.files])} />
      </label>
    </div>
    <div className="flex flex-col sm:flex-row gap-2 normal-case tracking-normal">
      <textarea data-testid="form-image-links-input" rows={2} value={links} onChange={(e) => setLinks(e.target.value)}
        placeholder="Arba įklijuokite tiekėjo nuotraukų nuorodas (po vieną eilutėje)" className={`${input} h-auto py-2 flex-1`} />
      <button type="button" data-testid="form-image-links-btn" onClick={fromLinks} disabled={busy}
        className="h-10 px-4 border border-[#3B4157] hover:border-orange-500 hover:text-orange-500 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50 sm:self-start">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Įkelti iš nuorodų
      </button>
    </div>
    </div>
  );
};

const Specs = ({ specs, setSpecs }) => {
  const upd = (i, k, v) => setSpecs(specs.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2">
      {specs.map((s, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2">
          <input data-testid={`form-spec-key-lt-${i}`} placeholder="Pavadinimas LT" className={input} value={s.key_lt} onChange={(e) => upd(i, "key_lt", e.target.value)} />
          <input data-testid={`form-spec-key-en-${i}`} placeholder="Name EN" className={input} value={s.key_en} onChange={(e) => upd(i, "key_en", e.target.value)} />
          <input data-testid={`form-spec-value-${i}`} placeholder="Reikšmė LT" className={input} value={s.value} onChange={(e) => upd(i, "value", e.target.value)} />
          <input data-testid={`form-spec-value-en-${i}`} placeholder="Value EN (nebūtina)" className={input} value={s.value_en || ""} onChange={(e) => upd(i, "value_en", e.target.value)} />
          <button type="button" onClick={() => setSpecs(specs.filter((_, j) => j !== i))} className="w-10 h-10 grid place-items-center text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" data-testid="form-add-spec-btn" onClick={() => setSpecs([...specs, { key_lt: "", key_en: "", value: "", value_en: "" }])} className="inline-flex items-center gap-1 text-sm text-orange-500"><Plus className="w-4 h-4" /> Pridėti parametrą</button>
    </div>
  );
};

const Options = ({ options, setOptions }) => {
  const get = (k) => options.find((o) => o.key === k);
  const upd = (k, patch) => {
    const cur = get(k) || { key: k, price: 0, enabled: false };
    setOptions([...options.filter((o) => o.key !== k), { ...cur, ...patch }].sort((a, b) => OPTION_KEYS.indexOf(a.key) - OPTION_KEYS.indexOf(b.key)));
  };
  return (
    <div className="space-y-2">
      {OPTION_KEYS.map((k) => {
        const o = get(k);
        return (
          <div key={k} className="grid grid-cols-[auto_1fr_120px] gap-3 items-center">
            <input type="checkbox" data-testid={`form-option-${k}-checkbox`} checked={!!o?.enabled} onChange={(e) => upd(k, { enabled: e.target.checked })} className="accent-orange-500 w-4 h-4" />
            <span className="text-sm normal-case tracking-normal text-slate-300">{OPTION_LT[k]}</span>
            <input data-testid={`form-option-${k}-price`} type="number" min="0" step="0.01" placeholder="Kaina €" className={input} value={o?.price ?? ""} onChange={(e) => upd(k, { price: e.target.value })} />
          </div>
        );
      })}
    </div>
  );
};

export default function ProductForm({ product, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...product });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!f.name || f.price === "") return toast.error("Įveskite pavadinimą ir kainą");
    setBusy(true);
    if (Number(f.discount_pct || 0) < 0 || Number(f.discount_pct || 0) > 90) return toast.error("Nuolaida turi būti 0–90 %");
    const body = { ...f, price: Number(f.price), discount_pct: Number(f.discount_pct || 0), stock: Number(f.stock || 0), compatible: (f.compatible || []).map((x) => x.trim()).filter(Boolean), options: (f.options || []).map((o) => ({ ...o, price: Number(o.price || 0) })) };
    delete body.id; delete body.views; delete body.created_at;
    try {
      product.id ? await api.put(`/admin/products/${product.id}`, body) : await api.post("/admin/products", body);
      toast.success("Išsaugota");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-form-dialog" className="bg-[#292D3C] border-[#3B4157] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase text-2xl">{product.id ? "Redaguoti sėdynę" : "Nauja sėdynė"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          {!product.id && <SupplierImport onFill={(d) => setF((x) => ({ ...x, ...d, sku: d.sku || x.sku, brand: BRANDS.includes(d.brand) ? d.brand : x.brand, images: [...x.images, ...d.images] }))} />}
          <Field label="Pavadinimas *" className="sm:col-span-2"><input data-testid="form-name-input" className={input} value={f.name} onChange={set("name")} /></Field>
          <Field label="Gamintojas"><select data-testid="form-brand-select" className={input} value={f.brand} onChange={set("brand")}>{BRANDS.map((b) => <option key={b}>{b}</option>)}</select></Field>
          <Field label="Kodas (SKU)"><input data-testid="form-sku-input" className={input} value={f.sku} onChange={set("sku")} /></Field>
          <Field label="Technika"><select data-testid="form-machine-select" className={input} value={f.machine_type} onChange={set("machine_type")}>{MACHINES.map((m) => <option key={m} value={m}>{MACHINE_LT[m]}</option>)}</select></Field>
          <Field label="Pakaba"><select data-testid="form-suspension-select" className={input} value={f.suspension} onChange={set("suspension")}>{SUSPENSIONS.map((s) => <option key={s} value={s}>{SUSP_LT[s]}</option>)}</select></Field>
          <PriceFields f={f} setF={setF} />
          <Field label="Likutis, vnt."><input data-testid="form-stock-input" type="number" min="0" className={input} value={f.stock} onChange={set("stock")} /></Field>
          <Field label="Modelis (pvz. AS2480 – variantams susieti)"><input data-testid="form-model-input" className={input} value={f.model || ""} onChange={set("model")} /></Field>
          <Field label="Tinka markei (tuščia = universalus)"><input data-testid="form-fits-input" className={input} value={f.fits || ""} onChange={set("fits")} /></Field>
          <Field label="Tinka traktoriams (per kablelį, pvz. Belarus/MTZ, Fendt)" className="sm:col-span-2"><input data-testid="form-compatible-input" className={input} value={(f.compatible || []).join(", ")}
            onChange={(e) => setF({ ...f, compatible: e.target.value.split(",").map((x) => x.trimStart()) })} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-price-on-request-checkbox" type="checkbox" checked={!!f.price_on_request} onChange={(e) => setF({ ...f, price_on_request: e.target.checked })} className="accent-orange-500 w-4 h-4" /> Vietoj kainos rodyti „Klausti kainos“
          </label>
          <Field label="Aprašymas LT" className="sm:col-span-2"><textarea data-testid="form-desc-lt-input" rows={3} className={`${input} h-auto py-2`} value={f.description_lt} onChange={set("description_lt")} /></Field>
          <Field label="Description EN" className="sm:col-span-2"><textarea data-testid="form-desc-en-input" rows={3} className={`${input} h-auto py-2`} value={f.description_en} onChange={set("description_en")} /></Field>
          <Field label="Nuotraukos (pirma – pagrindinė)" className="sm:col-span-2"><Images images={f.images} setImages={(images) => setF((x) => ({ ...x, images }))} /></Field>
          <Field label="Techniniai duomenys" className="sm:col-span-2"><Specs specs={f.specs} setSpecs={(specs) => setF((x) => ({ ...x, specs }))} /></Field>
          <Field label="Papildomi priedai (klientas siunčia užklausą, pristatymas iki 14 d.)" className="sm:col-span-2"><Options options={f.options || []} setOptions={(options) => setF((x) => ({ ...x, options }))} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-stocked-checkbox" type="checkbox" checked={!!f.stocked} onChange={(e) => setF({ ...f, stocked: e.target.checked })} className="accent-orange-500 w-4 h-4" /> Laikome sandėlyje (rodyti tarp „Sandėlio modelių“)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-premium-checkbox" type="checkbox" checked={f.premium !== false} onChange={(e) => setF({ ...f, premium: e.target.checked })} className="accent-orange-500 w-4 h-4" /> Rodyti „Premium“ ženklą
          </label>
          <GiftFields f={f} setF={setF} />
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-universal-checkbox" type="checkbox" checked={!!f.universal} onChange={(e) => setF({ ...f, universal: e.target.checked })} className="accent-orange-500 w-4 h-4" /> Tinka visiems traktoriams (rodyti filtruojant pagal bet kurią markę)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-free-shipping-checkbox" type="checkbox" checked={!!f.free_shipping} onChange={(e) => setF({ ...f, free_shipping: e.target.checked })} className="accent-orange-500 w-4 h-4" /> Nemokamas pristatymas (jei krepšelyje tik tokios prekės)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input data-testid="form-active-checkbox" type="checkbox" checked={!f.active} onChange={(e) => setF({ ...f, active: !e.target.checked })} className="accent-orange-500 w-4 h-4" /> Nerodyti sėdynės
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-11 px-5 border border-[#3B4157] text-sm hover:border-slate-500">Atšaukti</button>
            <button data-testid="form-save-btn" disabled={busy} className="h-11 px-6 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Išsaugoti
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
